import { Link, useParams } from "react-router-dom";
import Button from "@/components/Button";
import { ApolloError, useQuery } from "@apollo/client";
import { CHANGE_REQUESTS_QUERY, ChangeRequestStatus, Node } from "./queries";
import { type Path, useNavigate } from "@/router";
import type { Action } from "@/components/MenuButton";
import usePagination from "@/hooks/usePagination";
import { useEffect, useMemo, useState } from "react";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useParams as useUrlParams } from "@/hooks/useSearchParams";
import { ChangeRequestTable } from "@/features/global/components/ChangeRequestTable";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import ApproveModal from "./_components/ApproveModal";
import RejectModal from "./_components/RejectModal";
import CancelModal from "./_components/CancelModal";
import EditModal from "./_components/EditModal";
import EditsRequiredModal from "./_components/EditsRequiredModal";
import ReassignModal from "./_components/ReassignModal";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import SearchInput from "@/components/SearchInput";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import Select from "@/components/Select";
import { buildPersonChangeHistoryPath, formatStage, type FormattedChange } from "./_utils";
import { SectionDetailsHeader } from "../_components/SectionDetailsHeader";
import { SectionPeopleTabs } from "../_components/SectionPeopleTabs";
import { TableControls, TableHeading, TableFooter, buildActionColumn } from "@/components/Table";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { sendToRaygun } from "@/lib/raygunUtils";
import { NoteNotableValues } from "@/types";
import { useChangeRequestListDeepLink } from "./hooks/useChangeRequestListDeepLink";

type RowAction = {
  changeRequest: Node;
  action: "approve" | "reject" | "cancel" | "edit" | "editsRequired" | "reassign";
};

function editableRequest({ change, status }: Node) {
  let editableType = false;

  switch (change.__typename) {
    case "BackofficeUpdateUnlistedEmploymentChange":
    case "BackofficeAddUnlistedEmploymentChange":
      editableType = true;
  }

  return editableType && status === ChangeRequestStatus.PENDING;
}

const editsRequired = (node: Node) =>
  editableRequest(node) && "classification" in node.change && !node.change.classification;

function Modal({ rowAction, onCloseRowModal }: { rowAction?: RowAction; onCloseRowModal(): void }) {
  if (!rowAction) return;

  const Component = {
    approve: ApproveModal,
    edit: EditModal,
    editsRequired: EditsRequiredModal,
    reassign: ReassignModal,
    reject: RejectModal,
    cancel: CancelModal
  }[rowAction.action];

  return <Component changeRequest={rowAction.changeRequest} onClosed={onCloseRowModal} />;
}

const emptyCaption = "This section does not have any past or current change requests.";

function ChangeRequests() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const { params: urlParams, addSearchParam, deleteSearchParams } = useUrlParams();
  const stage = (urlParams.stage as ChangeRequestStatus) || undefined;
  const { setError } = useErrorBanner();

  const hasActiveFilters = !!debouncedSearch || !!stage;

  function setStage(value: ChangeRequestStatus | undefined) {
    if (value) {
      addSearchParam("stage", value);
    } else {
      deleteSearchParams("stage");
    }
  }

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, stage, resetPagination]);

  function onError(error: ApolloError) {
    sendToRaygun(error);
    setError(error.message);
  }

  const {
    data: currentData,
    previousData,
    loading
  } = useQuery(CHANGE_REQUESTS_QUERY, {
    onError,
    variables: { sectionId: sectionId ?? "", query: debouncedSearch || undefined, stage, ...paginationVariables },
    errorPolicy: "all",
    skip: !sectionId
  });
  const data = currentData || previousData;
  const [rowAction, setRowAction] = useState<RowAction>();
  const [noteTarget, setNoteTarget] = useState<{
    id: string;
    pgaId: string;
  } | null>(null);
  const { Menu, setMenu, reposition: repositionMenu } = useTableRowMenu();
  const [sideSheetPgaId, setSideSheetPgaId] = useState<string | null>(null);

  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewPeople });

  function onCloseRowModal() {
    setRowAction(undefined);
  }

  const { manageChangeformSettings = false, manageChangeRequests = false } = data?.section.permissions || {};

  const actionColumn = buildActionColumn<Node>({
    getActions: (changeRequest) => {
      const openModal = (action: RowAction["action"]) => () => {
        setRowAction({ action, changeRequest });
      };

      const isPending = changeRequest.status === ChangeRequestStatus.PENDING;
      const actions: Action[] = [];

      if (isPending) {
        actions.push({
          section: "Manage",
          text: "Approve Change Request",
          onClick: () => {
            openModal(editsRequired(changeRequest) ? "editsRequired" : "approve")();
          }
        });
        actions.push({
          section: "Manage",
          text: "Reassign to Different Section",
          onClick: openModal("reassign")
        });
        actions.push({
          section: "Manage",
          text: "Cancel Change Request",
          onClick: openModal("cancel")
        });
      }

      actions.push({
        section: "Actions",
        text: "View On Person Profile",
        onClick: () =>
          navigate(
            `/people/${changeRequest.person.pgaId}/person/change-requests?highlightRow=${changeRequest.id}` as Path
          )
      });
      actions.push({
        section: "Actions",
        text: "Add Note",
        onClick: () =>
          setNoteTarget({
            id: changeRequest.id,
            pgaId: changeRequest.person.pgaId
          })
      });

      if (isPending) {
        actions.push({
          text: "Reject Change Request",
          destructive: true,
          onClick: openModal("reject")
        });
      }

      return actions;
    },
    onViewClick: (changeRequest) => setSideSheetPgaId(changeRequest.person.pgaId),
    viewButtonTitle: "View Person",
    actionId,
    setMenu
  });

  function onEdit(changeRequest: Node, field: FormattedChange) {
    if (field.id === "classification" && editableRequest(changeRequest)) {
      return () => {
        setRowAction({ action: "edit", changeRequest });
      };
    }
  }

  const nodes = (data?.section.changeRequests.nodes || []).filter(Boolean) as Node[];
  const rowIds = useMemo(() => nodes.map((node) => node.id), [nodes]);

  const { expandChangesRowId, highlightRowId, containerRef } = useChangeRequestListDeepLink({
    loading,
    rowIds
  });

  if (!sectionId) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <Modal rowAction={rowAction} onCloseRowModal={onCloseRowModal} />
      <AddEditNoteSideSheet
        open={!!noteTarget}
        onClose={() => setNoteTarget(null)}
        notableId={noteTarget?.id || ""}
        notableType={NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST}
        refetchQueries={[CHANGE_REQUESTS_QUERY]}
        onSaveSuccess={() => setNoteTarget(null)}
        pgaId={noteTarget?.pgaId}
        objectType="person"
      />
      <PersonSideSheet
        open={!!sideSheetPgaId}
        onClose={() => setSideSheetPgaId(null)}
        pgaId={sideSheetPgaId}
        showPersonalDetails
        showEmployment
      />
      <SectionDetailsHeader sectionId={sectionId} />
      <SectionPeopleTabs />
      <Menu />
      <TableHeading
        heading="Member and Associate Data Change Requests"
        body="A list of all personal data changes that members and associates of this section have requested can be found below. Those that require manual approval before the data change is saved in the system are marked as 'Needs Approval'."
        actions={
          manageChangeformSettings
            ? [
                {
                  label: "Edit Settings",
                  onClick: () => {},
                  render: () => (
                    <Link to="settings">
                      <Button variant="outlined">Edit Settings</Button>
                    </Link>
                  )
                }
              ]
            : undefined
        }
      />
      <ChangeRequestTable
        loading={loading}
        data={nodes}
        hasActiveFilters={hasActiveFilters}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.section.changeRequests)} />}
        renderControls={() => (
          <TableControls
            pagination={createHeaderPagination(data?.section.changeRequests)}
            activeFilters={stage ? 1 : 0}
            useExpandedFilters
            expandedFiltersState={{ stage: !!stage }}
            onClearAllFilters={() => {
              clearSearch();
              setStage(undefined);
            }}
          >
            <SearchInput
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
              placeholder="Search People"
            />
            <div data-name="stage" className="min-w-[200px]">
              <Select
                value={stage || "ALL"}
                label="Stage"
                dropdownHeight="full"
                onSelect={(value) => setStage(value === "ALL" ? undefined : (value as ChangeRequestStatus))}
                options={[
                  { value: "ALL", displayText: "Show All", border: true },
                  ...Object.keys(ChangeRequestStatus).map((s) => ({
                    value: s,
                    displayText: formatStage(s as ChangeRequestStatus)
                  }))
                ]}
              />
            </div>
          </TableControls>
        )}
        emptyCaption={emptyCaption}
        onHorizontalScroll={repositionMenu}
        onEdit={manageChangeRequests ? onEdit : undefined}
        noteEditing={
          manageChangeRequests
            ? {
                enabled: true,
                notableType: NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST,
                refetchQueries: [CHANGE_REQUESTS_QUERY],
                objectType: "person"
              }
            : undefined
        }
        columns={[
          "submittedAt",
          "type",
          "pgaId",
          "fullName",
          "change",
          "notes",
          "status",
          ...(manageChangeRequests ? [actionColumn] : [])
        ]}
        getRowLink={(row) => {
          const pgaId = row.original.person.pgaId;
          if (!pgaId) return null;

          return {
            to: buildPersonChangeHistoryPath(pgaId) as Path
          };
        }}
        highlightRowId={highlightRowId}
        deepLinkExpandChangesRowId={expandChangesRowId}
      />
    </div>
  );
}

export default ChangeRequests;
